import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FaEdit, FaSave, FaFilePdf, FaDownload } from 'react-icons/fa';
import axios from 'axios';

interface PDFFormViewerProps {
  documentUrl: string;
  documentId: string;
  documentName: string;
  onSave?: (pdfBlob: Blob) => void;
  allowEdit?: boolean;
}

export const PDFFormViewer: React.FC<PDFFormViewerProps> = ({
  documentUrl,
  documentId,
  documentName,
  onSave,
  allowEdit = true
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [pdfSrc, setPdfSrc] = useState<string>('');
  const [resolvedUrl, setResolvedUrl] = useState<string>('');
  const blobUrlRef = useRef<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let isCancelled = false;

    async function loadPdf() {
      setLoading(true);
      setError(null);

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      if (!documentUrl) {
        setError('No document URL provided');
        setLoading(false);
        return;
      }

      const trimmedUrl = documentUrl.trim();
      const absoluteUrl = trimmedUrl.startsWith('http')
        ? trimmedUrl
        : `${window.location.origin}${trimmedUrl.startsWith('/') ? '' : '/'}${trimmedUrl}`;

      setResolvedUrl(absoluteUrl);

      try {
        const response = await axios.get<ArrayBuffer>(absoluteUrl, {
          responseType: 'arraybuffer',
          // Allow credentials for same-origin requests
          withCredentials: true,
          headers: {
            Accept: 'application/pdf'
          }
        });

        if (isCancelled) {
          return;
        }

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setPdfSrc(blobUrl);
        setLoading(false);
      } catch (err) {
        if (isCancelled) {
          return;
        }
        console.error('❌ Error loading PDF:', err);
        setError('Failed to load document');
        setLoading(false);
      }
    }

    loadPdf();

    return () => {
      isCancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [documentUrl, reloadToken]);

  // Handle saving the filled PDF
  const handleSave = async () => {
    try {
      // In a real implementation, we would:
      // 1. Get the filled PDF data from the iframe
      // 2. Save it to the backend
      // 3. Call onSave callback
      
      console.log('Saving filled PDF...');
      if (onSave) {
        // This would need proper implementation to extract PDF from iframe
        // For now, just notify that save was attempted
        alert('Document saved successfully!');
      }
    } catch (err) {
      console.error('Error saving PDF:', err);
      setError('Failed to save document');
    }
  };

  const handleDownload = () => {
    // Create a link to download the current PDF
    const link = document.createElement('a');
    link.href = pdfSrc || resolvedUrl || documentUrl;
    link.download = documentName || 'document.pdf';
    link.target = '_blank';
    link.click();
  };

  const handleRefresh = () => {
    // Force refresh the iframe
    setIframeKey(Date.now());
    setReloadToken((token) => token + 1);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status" />
        <p className="mt-2">Loading PDF form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Document</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" size="sm" onClick={handleRefresh}>
          Try Again
        </Button>
      </Alert>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{ 
        padding: '10px', 
        borderBottom: '1px solid #dee2e6',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div className="d-flex align-items-center gap-2">
          <FaFilePdf className="text-danger" size={20} />
          <span className="fw-medium">{documentName}</span>
          {allowEdit && (
            <span className="badge bg-success">
              <FaEdit className="me-1" />
              Fillable Form
            </span>
          )}
        </div>
        
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownload}
            title="Download PDF"
          >
            <FaDownload className="me-1" />
            Download
          </Button>
          
          {allowEdit && (
            <Button
              variant="success"
              size="sm"
              onClick={handleSave}
              title="Save filled form"
            >
              <FaSave className="me-1" />
              Save
            </Button>
          )}
        </div>
      </div>

      {/* PDF Viewer */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          pdfSrc ? (
            <iframe
              key={iframeKey}
              src={pdfSrc}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title={documentName}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" role="status" />
            </div>
          )
        )}
        
        {/* Info overlay for fillable forms */}
        {allowEdit && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: 'rgba(25, 135, 84, 0.9)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            fontSize: '14px',
            pointerEvents: 'none',
            zIndex: 10
          }}>
            <FaEdit className="me-2" />
            Click on form fields to fill
          </div>
        )}
      </div>
    </div>
  );
};
